import React, { forwardRef, useCallback, useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  Paper,
  Slide,
  Stack,
  Typography,
  styled,
  Autocomplete,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ProGrid from "../../Components/ProGrid/ProGrid";
import NameCell from "../../Components/ProGrid/components/NameCell";
import DeleteCell from "../../Components/ProGrid/components/DeleteCell";
import Layout from "../../Components/Layout";
import { STAUTES } from "../../../utils/constant";
import { GET_ALL_EMPLOYEES_V2, DELETE_EMPLOYEE, UPLOAD_EXCEL_EMPLOYEES, RAP_INFOS_DROPDOWNS } from "../../../utils/services/apiPath";
import { errorToast, successToast } from "../../../utils/toastHelper";
import usePageState from "../../../utils/customHooks/usePageState";
import useDebouncedFilter from "../../../utils/customHooks/useDebouncedFilter";
import { getApi, postApi, deleteApi } from "../../../utils/services";
import { R_EMPLOYEES, R_EMPLOYEES_LIST } from "../../../utils/permissionReferenceConfig";
import useColumn from "../../../utils/customHooks/useColumns";
import { subModPermissionSelector } from "../../store/features/permissions/permissionsSlice";
import { useSelector } from "react-redux";
import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import { employeeApprenticeNavigatorFormName, formatKeys, formatPayloadDate } from "../../../utils/formHelper";
import EmployeeListToolbar from "../../Components/Toolbars/EmployeeListToolbar";
import { createODataFilter, createODataQuery, convertMuiFilterToOData } from "../../../utils/services/oData";
import { ExpandMore } from "@mui/icons-material";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { convertToCSV, downloadCSV } from "../../../utils/csvExporter";
import "./EmployeeList.scss"

const DialogWrapper = styled(Dialog)(
  () => `
      .MuiDialog-paper {
        overflow: visible;
      }
`
);

const Employees = [
  {
    label: "First Name",
    required: true
  },
  {
    label: "Middle Name",
    required: false
  },
  {
    label: "Last Name",
    required: true
  },
  {
    label: "Job Title",
    required: true
  },
  {
    label: "Cell Phone",
    required: true
  },
  {
    label: "Office Phone",
    required: false
  },
  {
    label: "Whatsapp Number",
    required: false
  },
  {
    label: "Birth Date",
    required: false
  },
  {
    label: "Hourly Rate Amount",
    required: false
  },
  {
    label: "Fringe Rate Amount",
    required: false
  },
  {
    label: "Email",
    required: true
  },
  {
    label: "Address",
    required: true
  },
  {
    label: "Address2",
    required: false
  },
  {
    label: "City",
    required: true
  },
  {
    label: "State",
    required: true
  },
  {
    label: "Zip",
    required: true
  },
  {
    label: "Last Four Digit SSN",
    required: true
  },
  {
    label: "Internal EmployeeID",
    required: true
  },
  {
    label: "Is Union Employee?",
    required: false
  },
  {
    label: "Health Insurance",
    required: false
  },
  {
    label: "Retirement/Pension",
    required: false
  },
  {
    label: "Life Insurance",
    required: false
  },
  {
    label: "Vacation/Sick Time",
    required: false
  },
  {
    label: "Holiday Time",
    required: false
  },
  {
    label: "Apprentice Training",
    required: false
  },
  {
    label: "Supplementary Unemployment Benefits",
    required: false
  }
];

const fields = Employees?.map(field => ({
  label: field.label,
  key: field.label,
  fieldType: {
    type: "input"
  },
  validations: field.required ? [
    {
      rule: "required",
      errorMessage: `${field.label} is required`,
      level: "error"
    }
  ] : []
}));

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const betweenFilter = () => {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  return [{ field: "modifiedDate", startDate: formatPayloadDate(thirtyDaysAgo), endDate: formatPayloadDate(today) }];
};

const applicationStatuses = [
  { cardTitle: "Application Sent", filterSearch: "application sent", searchableFields: ["apprenticeProspectStatusType"] },
  { cardTitle: "Application Received", filterSearch: "application received", searchableFields: ["apprenticeProspectStatusType"] },
  { cardTitle: "Additional Documents Requested", filterSearch: "additional documents requests", searchableFields: ["apprenticeProspectStatusType"] },
  { cardTitle: "Additional Documents Received", filterSearch: "additional documents received", searchableFields: ["apprenticeProspectStatusType"] },
  { cardTitle: "Interview Scheduled", filterSearch: "interview scheduled", searchableFields: ["apprenticeProspectStatusType"] },
  { cardTitle: "Interview Complete", filterSearch: "interview complete", searchableFields: ["apprenticeProspectStatusType"] },
  { cardTitle: "Approved (Last 30 days)", betweenFields: betweenFilter("accepted"), filterSearch: "approved", searchableFields: ["apprenticeProspectStatusType"] },
  { cardTitle: "Rejected (Last 30 Days)", betweenFields: betweenFilter("rejected"), filterSearch: "rejected", searchableFields: ["apprenticeProspectStatusType"] },
  {
    cardTitle: "671s Without signatures",
    query: "formDocuments/$count($filter=isApprenticeSigned eq false and isProScoreSigned eq false and formName eq 'ETA Form Document') gt 0"
  },
  { cardTitle: "All Apprentices", filterSearch: "true", searchableFields: ["cast(isApprentice, 'Edm.String')"] },
  { cardTitle: "All Employees" }
];

export default function EmployeeList() {
  const {
    currentPage,
    pageSize,
    data: employeesData,
    rowCount,
    status,
    sortModel,
    searchString,
    filterModel,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString,
    setFilterModel
  } = usePageState({ pageSize: 10 });
  const [selectedCard, setSelectedCard] = useState("All Employees");
  const [selectedRaps, setSelectedRaps] = useState([]);
  const [cardCounts, setCardCounts] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);

  const { canView, canAdd, canImport, canExport, canDelete, ...rest } = useSelector(subModPermissionSelector(R_EMPLOYEES, R_EMPLOYEES_LIST));
  const { formPermissions = [] } = rest || {};
  const apprenticeNavigatorPermission = formPermissions?.find(permission => permission.formName === employeeApprenticeNavigatorFormName) || {};

  const [isOpen, setIsOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [betweenFields, setBetweenFields] = useState([]);
  const [query, setQuery] = useState("");
  const [rapDropdownOptions, setRapDropdownOptions] = useState([]);
  const [applicationStatusCards] = useState(applicationStatuses);
  const [rapQuery, setRapQuery] = useState("");
  const onFilterChange = useDebouncedFilter(setFilterModel);
  const apprenticeNavigatorEnabled = useFeatureFlagEnabled(
    "apprentice-navigator-enabled"
  )

  const columns = useColumn(
    [
      {
        field: "firstName",
        headerName: "First Name",
        renderCell: params => <NameCell params={params} getRedirectUrl={params => params.row.employeeId} />
      },
      {
        field: "lastName",
        headerName: "Last Name"
      },
      {
        field: "internalEmployeeId",
        headerName: "Internal Employee ID",
      },
      {
        field: "jobTitle",
        headerName: "Position/Job Title"
      },
      {
        field: "projectName",
        headerName: "Project Name"
      },
      {
        field: "employeeStatusType",
        headerName: "Status"
      },
      {
        field: "isUnion",
        headerName: "Union Employee",
        renderCell: params => (params.value ? "Yes" : "No")
      },
      {
        field: "employeeType",
        headerName: "Type"
      },
      {
        field: "apprenticeProspectStatusType",
        headerName: "Apprentice Status"
      },
      {
        field: "employeeId",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell params={params} onDelete={onDelete} />
      }
    ],
    canDelete
  );

  const clearFilters = () => {
    setFilterSearch("");
    setBetweenFields([]);
    setQuery("");
  };

  const toggleSelectedRaps = (event, selected) => {
    if (selected.length === 0) {
      setRapQuery("");
      setSelectedRaps([]);
      return;
    }
    const selectedGuids = selected.map((selection) => selection.value)
    setSelectedRaps(selected)
    setRapQuery(`rapInfoId in (${selectedGuids.join()})`)
  }

  const RapMultiSelect = () => {
    return (
      <Stack>
        <Autocomplete
          multiple
          id="tags-standard"
          options={rapDropdownOptions}
          getOptionLabel={(option) => `${option.label}`}
          onChange={(e, value) => toggleSelectedRaps(e, value)}
          value={selectedRaps}
          renderInput={params => <TextField {...params} variant="standard" label="Apprentices By RAP" placeholder="RAP" />}
        />
      </Stack>
    );
  };

  const handleOpenDialogPopup = () => {
    setIsOpen(true);
  };

  const handleCloseDialogPopup = () => {
    setIsOpen(false);
  };

  const handleSubmit = async ({ validData }) => {
    const excelData = formatKeys(validData);
    const payload = { excelData };
    const { error } = await postApi(UPLOAD_EXCEL_EMPLOYEES, payload);
    if (error) return errorToast(error);
    handleOpenDialog();
    setMessage("Added Excel Data Successfully.");
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    getEmployeeList();
  };

  const onDelete = async id => {
    const { error } = await deleteApi(DELETE_EMPLOYEE, [{ employeeId: id }]);
    if (error) return errorToast(error);
    successToast("Employee deleted successfully.");
    getEmployeeList();
  };

  const getEmployeeList = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const searchableFields = [
      "firstName",
      "lastName",
      "internalEmployeeId",
      "jobTitle",
      "projectName",
      "employeeStatusType",
      "apprenticeProspectStatusType",
      "cast(isApprentice, 'Edm.String')"
    ];
    
    // Convert MUI filter model to OData filter
    const gridFilterQuery = convertMuiFilterToOData(filterModel);
    
    // Combine all queries including grid filter
    const allQueries = [query, rapQuery, gridFilterQuery].filter(q => q && q.length > 0);
    
    const countQueries = getEmployeeListCountQueries();
    const { data, totalRecords, error, headers } = await getApi(
      GET_ALL_EMPLOYEES_V2 +
      createODataQuery(
        searchableFields,
        betweenFields,
        allQueries,
        searchString.length === 0 ? filterSearch : searchString,
        sortModel,
        pageSize,
        currentPage
      ),
      { 
        headers: { 
          "x-counts": JSON.stringify(countQueries) 
        } 
      }
    );
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
    const queryCounts = headers["x-counts"] ? JSON.parse(headers["x-counts"]) : [];
    setCardCounts(queryCounts);
  }, [currentPage, pageSize, sortModel, searchString, filterSearch, query, rapQuery, filterModel, betweenFields, setStatus, setPageState]);

  useEffect(() => {
    setSelectedCard("All Employees");
  }, [searchString]);

  const getRapDropDown = async () => {
    const { data, error } = await getApi(RAP_INFOS_DROPDOWNS)
    if (error) return errorToast(error);

    setRapDropdownOptions(data);
  };

  const setFilters = ({ query = "", betweenFields = [], filterSearch = "" }) => {
    setFilterSearch(filterSearch);
    setQuery(query);
    setBetweenFields(betweenFields);

    if (filterSearch) {
      setFilterSearch(filterSearch);
    }

    if (query) {
      setQuery(query);
    }

    if (betweenFields) {
      setBetweenFields(betweenFields);
    }
  };

  const cardClick = title => {
    if (title === selectedCard || searchString.length > 0) {
      return;
    }
    setSelectedCard(title);
  };

  const getEmployeeListCountQueries = () => {
    try {
      const allSearchableFields = [
        "firstName",
        "lastName",
        "internalEmployeeId",
        "jobTitle",
        "projectName",
        "employeeStatusType",
        "apprenticeProspectStatusType",
        "cast(isApprentice, 'Edm.String')"
      ];
  
      return applicationStatusCards.map(card => {
        const betweenFieldsArray = card.cardBetweenFields || [];
        const filterSearchString = card.filterSearch || "";
        const queryString = card.query || "";
        const searchFilter = searchString.length > 0
          ? createODataFilter(allSearchableFields, betweenFields, searchString, "")
          : "";
        return createODataQuery(
          card.searchableFields,
          betweenFieldsArray,
          [queryString, rapQuery, searchFilter],
          filterSearchString,
          sortModel,
          0,
          0,
          false
        );
      });
    } catch (error) {
      return errorToast(error);
    }
  };

  const handleDownloadCSV = useCallback(async () => {
    const searchableFields = [
      "firstName",
      "lastName",
      "internalEmployeeId",
      "jobTitle",
      "projectName",
      "employeeStatusType",
      "apprenticeProspectStatusType",
      "email",
      "cast(isApprentice, 'Edm.String')"
    ];
  
    try {
      // The API caps $top, so fetch in batches. Batches run sequentially: the first
      const batchSize = 1000;
      const first = await getApi(
        GET_ALL_EMPLOYEES_V2 + createODataQuery(searchableFields, [], [], "", [], batchSize, 0, true)
      );
      if (first.error) return errorToast(first.error);

      const data = [...(first.data || [])];
      const total = first.totalRecords;
      for (let page = 1; page * batchSize < total; page++) {
        const { data: batch, totalRecords, error } = await getApi(
          GET_ALL_EMPLOYEES_V2 + createODataQuery(searchableFields, [], [], "", [], batchSize, page, true)
        );
        if (error) return errorToast(error);
        if (!batch?.length) return errorToast("The server returned fewer employees than expected. Please try again.");
        // A changed count means employees were added/removed mid-export and pages may have shifted.
        if (totalRecords !== total) return errorToast("The employee list changed during export. Please try again.");
        data.push(...batch);
      }

      if (!data.length) return errorToast("No data to export.");
  
      const sortedData = [...data].sort((a, b) =>
        (a.firstName || "").localeCompare(b.firstName || "")
      );
  
      const filteredData = sortedData.map(item => ({
        "First Name": item.firstName,
        "Last Name": item.lastName,
        "Internal Employee ID": item.internalEmployeeId,
        "Job Title": item.jobTitle,
        "Project Name": item.projectName, 
        "Status": item.employeeStatusType,
        "Union Employee": item.isUnion ? "Yes" : "No",
        "Type": item.employeeType,
        "Apprentice Status": item.apprenticeProspectStatusType,
        "Email": item.email
      }));
  
      const csv = convertToCSV(filteredData);
      downloadCSV(csv, "EmployeeList.csv");
  
    } catch (err) {
      errorToast("An unexpected error occurred.");
    }
  }, []);

  useEffect(() => {
    const card = applicationStatusCards.filter(card => card.cardTitle === selectedCard)[0];
    if (selectedCard === "All Employees") {
      clearFilters();
    } else {
      setFilters(card);
    }
  }, [selectedCard]);

  useEffect(() => {
    if (!apprenticeNavigatorEnabled) {
      return
    }
  }, [rapQuery, searchString]);

  useEffect(() => {
    getEmployeeList();
  }, [getEmployeeList]);

  useEffect(() => {
    if (!apprenticeNavigatorEnabled) {
      return
    }
    getRapDropDown();
  }, []);

  return (
    <Layout canView={canView}>
      {(apprenticeNavigatorPermission.canView && apprenticeNavigatorEnabled) && (
        <div className="rs-grid-wrapper v2">
          <Accordion defaultExpanded style={{ padding: "5px", marginTop: "10px", marginBottom: "10px" }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Apprentice Navigator</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} direction={"row"}>
                {applicationStatusCards.map((obj, i) => {
                  return (
                    <Grid key={obj.cardTitle} item xs={4}>
                      {
                        <Paper
                          id={obj.cardTitle}
                          onClick={e => cardClick(obj.cardTitle)}
                          elevation={3}
                          sx={{
                            textAlign: "justify",
                            width: "100%",
                            fontSize: "15px",
                            fontWeight: obj.cardTitle === selectedCard ? "bold" : "normal",
                            backgroundColor: obj.cardTitle === selectedCard ? "#1f88e6" : "#ffff",
                            color: obj.cardTitle === selectedCard ? "#ffff" : null
                          }}
                        >
                          <div style={{ display: "flex", flexDirection: "row" }}>
                            <div style={{ whiteSpace: "nowrap", paddingTop: "15px", paddingBottom: "15px", paddingLeft: "15px" }}>
                              {obj.cardTitle}
                            </div>
                            <b style={{ paddingTop: "15px", paddingRight: "15px", paddingBottom: "15px" }}>({cardCounts[i]})</b>
                          </div>
                        </Paper>
                      }
                    </Grid>
                  );
                })}
                <Grid item xs={4}>
                  <RapMultiSelect />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </div>
      )}
      <ProGrid
        title="Employees"
        loading={status === STAUTES.LOADING}
        options={{
          getRowId: row => row.employeeId,
          rowCount: rowCount,
          paginationMode: "server",
          paginationModel: { pageSize: pageSize, page: currentPage },
          onPaginationModelChange: setPagination,
          pageSizeOptions: [10, 25, 50],
          sortingMode: "server",
          filteringMode: "server",
          onSortModelChange: setSortModel,
          onFilterModelChange: onFilterChange
        }}
        columns={columns}
        rows={employeesData}
        searchMode={!!searchString}
        toolbar={EmployeeListToolbar({
          importExcelAction: handleOpenDialogPopup,
          searchModel: { defaultValue: searchString, handleDebounce: setSearchString },
          hideAddButton: !canAdd,
          hideImport: !canImport,
          hideExport: !canExport,
          handleDownloadCSV
        })}
      />
      <ReactSpreadsheetImport isOpen={isOpen} onClose={handleCloseDialogPopup} onSubmit={handleSubmit} fields={fields} />
      <DialogWrapper open={openDialog} maxWidth="sm" fullWidth TransitionComponent={Transition} keepMounted onClose={handleCloseDialog}>
        <Box sx={{ padding: "20px" }}>
          <Typography fontSize={15} fontWeight={600}>
            {message}
          </Typography>
          <Button fullWidth size="large" variant="contained" sx={{ mt: 2 }} onClick={handleCloseDialog}>
            Close
          </Button>
        </Box>
      </DialogWrapper>
    </Layout>
  );
}
